import React, { useEffect, useState } from "react";

import { FormattedMessage, useIntl } from "react-intl";

import { ACTIVITY_TYPES } from "Constants/constants";
import { Button } from "ds/Button";

import * as S from "./CancelActivityModal.styles";

import type { Activity } from "@packages/client";

const CancelActivityModal = ({
  isOpen,
  onOpenChange,
  isCancellable,
  onCancelActivity,
  id,
  isPending,
  recentInprogressActivities,
  recentCompletedActivities
}: {
  recentInprogressActivities: Activity[];
  recentCompletedActivities: Activity[];
  isOpen: boolean;
  onOpenChange: React.Dispatch<React.SetStateAction<string>>;
  isCancellable: boolean;
  isPending: boolean;
  onCancelActivity: () => void;
  id: string;
  title: React.ReactNode;
}) => {
  const intl = useIntl();
  const [showNoLongerCancellable, setShowNoLongerCancellable] = useState(false);
  const [act, setAct] = useState<Activity | undefined>(undefined);

  useEffect(() => {
    const activity =
      recentInprogressActivities?.filter(
        item => item.type === ACTIVITY_TYPES.ENVIRONMENT.SOURCE_OPERATION
      )[0] ||
      recentInprogressActivities?.filter(
        item => item.type === ACTIVITY_TYPES.ENVIRONMENT.OPERATION
      )[0];
    const activityCompleted = recentCompletedActivities?.[0];
    setAct(activity || activityCompleted);
  }, [recentCompletedActivities, recentInprogressActivities]);

  const branch = act?.payload?.environment?.id;
  const operationName =
    act?.type === ACTIVITY_TYPES.ENVIRONMENT.SOURCE_OPERATION
      ? act?.payload?.operation
      : act?.payload?.operation_name;
  const sourceOperationType =
    act?.type === ACTIVITY_TYPES.ENVIRONMENT.SOURCE_OPERATION;

  return (
    <S.CancelModal
      isOpen={isOpen}
      onOpenChange={() => onOpenChange(isOpen ? "" : id)}
    >
      {!showNoLongerCancellable ? (
        <>
          <S.CancelModalHeader>
            <FormattedMessage id="activity.cancel_modal.header" />
          </S.CancelModalHeader>
          {!isPending ? (
            <div>
              <FormattedMessage
                id="activity.cancel_operation_modal.description"
                values={{
                  operationType: sourceOperationType
                    ? "source operation"
                    : "runtime operation",
                  activityName: operationName,
                  branch: branch,
                  b: (...chunks) => <b>{chunks}</b>,
                  strong: (...chunks) => <S.Description>{chunks}</S.Description>
                }}
              />
            </div>
          ) : (
            <div>
              <FormattedMessage
                id="activity.cancel_modal.description"
                values={{
                  activityName: operationName,
                  branch
                }}
              />
            </div>
          )}
          {!isPending && (
            <span>
              <FormattedMessage id="activity.cancel_modal.note" />
            </span>
          )}
          <S.CancelModalButtonGroup>
            <Button
              variant="secondary"
              onClick={() => onOpenChange("")}
              analyticId="activity.cancel_modal.cancel"
            >
              <FormattedMessage id="activity.cancel_modal.cancel" />
            </Button>
            <Button
              analyticId="activity.cancel_modal.cancel_operation"
              onClick={() => {
                if (!isCancellable) return setShowNoLongerCancellable(true);
                onCancelActivity();
                onOpenChange("");
              }}
            >
              <FormattedMessage id="activity.cancel_modal.cancel_operation" />
            </Button>
          </S.CancelModalButtonGroup>
        </>
      ) : (
        <>
          <S.CancelModalHeader>
            <FormattedMessage id="activity.cancel_modal.error.header" />
          </S.CancelModalHeader>
          <p>
            <FormattedMessage id="activity.cancel_modal.error.description" />
          </p>
          <S.CancelModalButtonGroup spaceBetween>
            <Button
              variant="link"
              href={intl.formatMessage({
                id: "links.documentation.environments.cancel_activity"
              })}
            >
              <FormattedMessage id="activity.cancel_modal.error.learn_more" />
            </Button>
            <Button onClick={() => onOpenChange("")}>
              <FormattedMessage id="activity.cancel_modal.error.okay" />
            </Button>
          </S.CancelModalButtonGroup>
        </>
      )}
    </S.CancelModal>
  );
};

export default CancelActivityModal;
